import { rectsIntersect, type Rect, type Stroke } from './stroke';

/**
 * Ray-cast point-in-polygon test (even-odd rule). Concave-safe.
 * `poly` is a flat [x0,y0,x1,y1,...] ring; the last vertex implicitly closes
 * back to the first.
 */
export function pointInPolygon(x: number, y: number, poly: number[]): boolean {
  const n = poly.length / 2;
  if (n < 3) return false;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = poly[i * 2]!, yi = poly[i * 2 + 1]!;
    const xj = poly[j * 2]!, yj = poly[j * 2 + 1]!;
    const intersect = (yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** A stroke is selected when strictly more than 50% of its points fall inside the polygon. */
export function strokeSelected(points: Float32Array, poly: number[]): boolean {
  const n = points.length / 4;
  if (n === 0) return false;
  let count = 0;
  for (let i = 0; i < n; i++) {
    if (pointInPolygon(points[i * 4]!, points[i * 4 + 1]!, poly)) count++;
  }
  return count > n / 2;
}

function polygonBbox(poly: number[]): Rect {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i < poly.length; i += 2) {
    const x = poly[i]!, y = poly[i + 1]!;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  if (!isFinite(minX)) return { x: 0, y: 0, w: 0, h: 0 };
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** Bbox-prefilter against the polygon's bounding box, then the >50% point test. */
export function selectStrokes(strokes: Stroke[], poly: number[]): Stroke[] {
  const pb = polygonBbox(poly);
  return strokes.filter((s) => rectsIntersect(s.bbox, pb) && strokeSelected(s.points, poly));
}

/**
 * Scale + rotate about (cx, cy), then translate by (dx, dy). Pressure and dt
 * pass through unchanged. Returns a new buffer; the input is not mutated.
 */
export function transformPoints(
  points: Float32Array,
  t: { dx: number; dy: number; scale: number; rotate: number; cx: number; cy: number },
): Float32Array {
  const out = new Float32Array(points.length);
  const cos = Math.cos(t.rotate), sin = Math.sin(t.rotate);
  for (let i = 0; i + 3 < points.length; i += 4) {
    const x = points[i]!, y = points[i + 1]!;
    const sx = (x - t.cx) * t.scale;
    const sy = (y - t.cy) * t.scale;
    const rx = sx * cos - sy * sin;
    const ry = sx * sin + sy * cos;
    out[i] = t.cx + rx + t.dx;
    out[i + 1] = t.cy + ry + t.dy;
    out[i + 2] = points[i + 2]!;
    out[i + 3] = points[i + 3]!;
  }
  return out;
}

/** Union of the given strokes' bboxes; zero rect for an empty selection. */
export function selectionBbox(strokes: Stroke[]): Rect {
  if (strokes.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const s of strokes) {
    const b = s.bbox;
    if (b.x < minX) minX = b.x;
    if (b.y < minY) minY = b.y;
    if (b.x + b.w > maxX) maxX = b.x + b.w;
    if (b.y + b.h > maxY) maxY = b.y + b.h;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}
